"use client"

import { useState } from "react"
import AirTicketForm from "./AirTicketForm"
import HotelForm from "./HotelForm"
import VisaForm from "./VisaForm"
import InsuranceForm from "./InsuranceForm"
import GenericItemForm from "./GenericItemForm"
import "./Sales.css"

const ItemManagement = ({ items, onItemsChange }) => {
  const [showAddItem, setShowAddItem] = useState(false)
  const [selectedItemType, setSelectedItemType] = useState("")

  const itemTypes = [
    { value: "air_ticket", label: "Air Ticket", description: "Flight bookings and airline tickets" },
    { value: "hotel", label: "Hotel", description: "Hotel reservations and accommodations" },
    { value: "visa", label: "Visa", description: "Visa processing and applications" },
    { value: "insurance", label: "Insurance", description: "Travel and health insurance" },
    { value: "baggage", label: "Baggage", description: "Extra baggage allowance" },
    { value: "visa_message", label: "Visa Message", description: "Visa consultation services" },
    { value: "ticketing", label: "Ticketing", description: "General ticketing services" },
    { value: "real_estate", label: "Real Estate", description: "Property related services" },
    { value: "visa_assistance", label: "Visa Assistance", description: "Visa application help" },
    { value: "attractions", label: "Attractions", description: "Tourist attractions and tours" },
    { value: "umrah_service", label: "Umrah Service", description: "Religious pilgrimage services" },
    { value: "b2b_khasab", label: "B2B Khasab", description: "Khasab tour packages" },
    { value: "security_deposit", label: "Security Deposit", description: "Refundable deposits" },
    { value: "typing_service", label: "Typing Service", description: "Document typing services" },
    { value: "burj_khalifa", label: "Burj Khalifa", description: "Burj Khalifa tour tickets" },
    { value: "desert_safari", label: "Desert Safari", description: "Desert adventure tours" },
    { value: "seat_pre_book", label: "Seat Pre-booking", description: "Airline seat reservations" },
    { value: "food", label: "Food", description: "Meal arrangements and catering" },
    { value: "others", label: "Others", description: "Other miscellaneous services" },
  ]

  // ✅ Accepts formData directly from child forms
  const handleAddItem = (itemData) => {
    const qty = Number(itemData.quantity) || 1
    const sell = Number(itemData.selling_price) || 0
    const total = Number(itemData.total_amount) || (qty * sell)

    const newItem = {
      id: Date.now() + Math.random(),
      ...itemData,
      total_amount: total,
      totalAmount: total, // 👈 Added camelCase for SalesForm compatibility
      created_at: new Date().toISOString(),
    }

    onItemsChange([...items, newItem])
    setShowAddItem(false)
    setSelectedItemType("")
  }

  const handleRemoveItem = (itemId) => {
    const updatedItems = items.filter((item) => item.id !== itemId)
    onItemsChange(updatedItems)
  }

  const getTotalAmount = () => {
    return items.reduce((sum, item) => sum + (item.total_amount || 0), 0)
  }

  const renderItemForm = () => {
    const commonProps = {
      onSubmit: handleAddItem,
      onCancel: () => {
        setShowAddItem(false)
        setSelectedItemType("")
      },
    }

    switch (selectedItemType) {
      case "air_ticket":
        return <AirTicketForm {...commonProps} />
      case "hotel":
        return <HotelForm {...commonProps} />
      case "visa":
        return <VisaForm {...commonProps} />
      case "insurance":
        return <InsuranceForm {...commonProps} />
      default:
        return <GenericItemForm {...commonProps} itemType={selectedItemType} />
    }
  }

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount)
  }

  return (
    // ⛔ Stop ANY submit event from child forms bubbling up to the parent SalesForm
    <div className="item-management" onSubmit={(e) => e.stopPropagation()}>
      <div className="section-header">
        <h4>Items & Services</h4>
        <button type="button" className="btn-primary" onClick={() => setShowAddItem(true)}>
          + Add Item
        </button>
      </div>

      {items.length > 0 && (
        <div className="items-summary">
          <div className="summary-stats">
            <div className="stat-item">
              <label>Total Items:</label>
              <span>{items.length}</span>
            </div>
            <div className="stat-item">
              <label>Total Amount:</label>
              <span className="amount">{formatCurrency(getTotalAmount())}</span>
            </div>
          </div>
        </div>
      )}

      {showAddItem && !selectedItemType && (
        <div className="item-type-selector">
          <h5>Select Item Type</h5>
          <div className="item-types-grid">
            {itemTypes.map((type) => (
              <div
                key={type.value}
                className="item-type-card"
                onClick={() => setSelectedItemType(type.value)}
              >
                <h6>{type.label}</h6>
                <p>{type.description}</p>
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setShowAddItem(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAddItem && selectedItemType && (
        <div className="item-form-container">
          <h5>Add {itemTypes.find((t) => t.value === selectedItemType)?.label}</h5>
          {renderItemForm()}
        </div>
      )}

      {items.length > 0 && (
        <div className="items-list">
          <h5>Added Items</h5>
          <div className="items-table">
            <table>
              <thead>
                <tr>
                  <th>Item / Service Type</th>
                  <th>Supplier Name</th>
                  <th>Base Amount</th>
                  <th>Tax / Fees</th>
                  <th>Discount</th>
                  <th>Loss / Margin</th>
                  <th>Total Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const cost = Number(item.cost_price || 0)
                  const sell = Number(item.selling_price || 0)
                  const tax = Number(item.tax || item.tax_amount || 0)
                  const discount = Number(item.discount || item.discount_amount || 0)
                  const marginAmt = sell - cost
                  const marginPct = cost > 0 ? ((marginAmt / cost) * 100).toFixed(1) : (item.margin || 0)
                  const suppName = item.supplier_name || (item.supplier_id ? `Supplier #${item.supplier_id}` : "—")

                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="item-info">
                          <div className="item-name">{item.item_name || item.service_type || "Item"}</div>
                          <span className="item-type-badge">
                            {itemTypes.find((t) => t.value === item.item_type)?.label || item.item_type || "Service"}
                          </span>
                        </div>
                      </td>
                      <td className="text-muted">{suppName}</td>
                      <td>{formatCurrency(sell)}</td>
                      <td>{formatCurrency(tax)}</td>
                      <td>{formatCurrency(discount)}</td>
                      <td style={{ color: marginAmt >= 0 ? "var(--bs-success, green)" : "red", fontWeight: 600 }}>
                        {formatCurrency(marginAmt)} ({marginPct}%)
                      </td>
                      <td className="total-amount" style={{ fontWeight: 700 }}>{formatCurrency(item.total_amount || (sell + tax - discount))}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="btn-sm btn-danger"
                            onClick={() => handleRemoveItem(item.id)}
                            title="Remove"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {items.length === 0 && !showAddItem && (
        <div className="no-items">
          <p>No items added yet. Click "Add Item" to get started.</p>
        </div>
      )}
    </div>
  )
}

export default ItemManagement
